import { useState, useEffect, useLayoutEffect, useCallback } from "react";
import axios from "axios";
import {
  Search, ChevronLeft, ChevronRight, RefreshCw,
  History, X
} from "lucide-react";
import { API_BASE } from "../config";

interface WalletItem {
  _id: string;
  name: string;
  email: string;
  active: boolean;
  createdAt: string;
  balance: string;
}

interface WalletHistoryItem {
  _id: string;
  email: string;
  previousAmount: string;
  updatedAmount: string;
  updateAmount: string;
  updateType: string;
  createdAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const WalletUpdate: React.FC = () => {
  const [walletData, setWalletData] = useState<WalletItem[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});
  const [debitValues, setDebitValues] = useState<Record<string, string>>({});
  const [creditValues, setCreditValues] = useState<Record<string, string>>({});

  // History Modal State
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState("");
  const [historyData, setHistoryData] = useState<WalletHistoryItem[]>([]);
  const [historyPagination, setHistoryPagination] = useState<PaginationInfo>({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useLayoutEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [search, pagination.limit]);

  const fetchWalletData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: String(pagination.page),
        limit: String(pagination.limit),
        searchTerm: search,
      });
      const response = await axios.get(`${API_BASE}/api/wallet/all?${params.toString()}`);

      if (response.data.status === "success") {
        setWalletData(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error("Error fetching wallet data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit, search]);

  const fetchHistoryData = useCallback(async (email: string, page: number = 1) => {
    try {
      setIsHistoryLoading(true);
      const response = await axios.get(`${API_BASE}/api/wallet/history?email=${email}&page=${page}&limit=${historyPagination.limit}`);
      if (response.data.status === "success") {
        setHistoryData(response.data.history);
        setHistoryPagination(response.data.pagination);
      }
    } catch (error) {
      console.error("Error fetching history data:", error);
    } finally {
      setIsHistoryLoading(false);
    }
  }, [historyPagination.limit]);

  useEffect(() => {
    fetchWalletData();
  }, [fetchWalletData]);

  useEffect(() => {
    if (showHistoryModal && selectedUserEmail) {
      fetchHistoryData(selectedUserEmail, 1);
    }
  }, [showHistoryModal, selectedUserEmail, fetchHistoryData]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleHistoryPageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= historyPagination.totalPages) {
      fetchHistoryData(selectedUserEmail, newPage);
    }
  };

  const handleTransaction = async (email: string, amountStr: string, type: "debit" | "credit") => {
    const amountNum = parseFloat(amountStr);
    if (isNaN(amountNum) || amountNum <= 0) {
      alert("Please enter a valid amount");
      return;
    }

    const currentBalance = parseFloat(walletData.find(w => w.email === email)?.balance || "0");
    let nextBalance = type === "credit" ? currentBalance + amountNum : currentBalance - amountNum;

    if (nextBalance < 0) {
      alert("Insufficient balance for debit");
      return;
    }

    setIsUpdating(prev => ({ ...prev, [email]: true }));
    try {
      await axios.post(`${API_BASE}/api/wallet/updateWallet`, {
        email,
        amount: nextBalance.toFixed(2),
      });

      // Update local state
      setWalletData(prev => prev.map(w =>
        w.email === email ? { ...w, balance: nextBalance.toFixed(2) } : w
      ));

      // Clear input
      if (type === "debit") setDebitValues(prev => ({ ...prev, [email]: "" }));
      else setCreditValues(prev => ({ ...prev, [email]: "" }));

    } catch (error) {
      console.error("Error updating wallet:", error);
      alert("Failed to update wallet balance");
    } finally {
      setIsUpdating(prev => ({ ...prev, [email]: false }));
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6 pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6 text-center md:text-left">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#523680] tracking-tight">Wallet Management</h1>
          <p className="mt-1 text-sm md:text-base text-gray-500 font-medium">Manage user balances and view transaction history</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchWalletData()}
            className="p-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all shadow-sm group"
            title="Refresh"
          >
            <RefreshCw className={`h-5 w-5 text-gray-400 group-hover:text-[#523680] ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4 items-center">
        <div className="relative flex-grow w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search Name or Email..."
            className="w-full pl-11 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-[#523680]/20 transition-all text-gray-800 placeholder-gray-400 font-medium"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select
            value={pagination.limit}
            onChange={(e) => {
              setPagination((prev) => ({ ...prev, limit: parseInt(e.target.value, 10), page: 1 }));
            }}
            className="flex-grow sm:flex-none px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-[#523680]/20 text-gray-700 font-bold text-sm"
          >
            {[10, 20, 50, 100].map(limit => (
              <option key={limit} value={limit}>{limit} / Page</option>
            ))}
          </select>
          <button
            onClick={() => fetchWalletData()}
            className="sm:hidden p-3 bg-gray-50 border-none rounded-xl text-[#523680]"
          >
            <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Wallet Cards */}
      <div className="grid grid-cols-1 gap-4">
        {isLoading && walletData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#523680] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : walletData.length > 0 ? (
          walletData.map((user) => (
            <div key={user._id} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300">
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">User Name</p>
                  <h3 className="text-base font-bold text-[#523680] truncate pr-2">{user.name}</h3>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold whitespace-nowrap border ${user.active ? 'bg-green-50 text-green-600 border-green-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                    {user.active ? 'Active' : 'Inactive'}
                  </div>
                  <button
                    onClick={() => {
                      setSelectedUserEmail(user.email);
                      setShowHistoryModal(true);
                    }}
                    className="p-2 bg-gray-50 rounded-lg text-gray-400 hover:text-[#523680] transition-all"
                    title="History"
                  >
                    <History className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-8 mb-6 pt-4 border-t border-gray-50">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Email</p>
                  <p className="text-sm font-medium text-gray-600 truncate">{user.email}</p>
                </div>

                <div className="min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Balance</p>
                  <p className="text-lg font-black text-[#523680] leading-tight">
                    ${parseFloat(user.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>

                <div className="min-w-0 sm:col-span-2 lg:col-span-1 flex justify-between items-end border-t sm:border-t-0 pt-3 sm:pt-0 border-gray-50">
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Joined</p>
                    <p className="text-sm font-medium text-gray-500">{new Date(user.createdAt).toLocaleDateString("en-GB")}</p>
                  </div>
                  <div className="text-[8px] font-mono text-gray-300 md:hidden">ID: {user._id.slice(-6)}</div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4 border-t border-gray-50">
                <div className="flex gap-2 flex-grow">
                  <input
                    type="number"
                    placeholder="Debit"
                    className="w-full px-3 py-2.5 bg-red-50/30 border border-red-100 rounded-xl focus:ring-2 focus:ring-red-500/20 outline-none text-sm font-bold text-red-700 placeholder-red-300 shadow-sm"
                    value={debitValues[user.email] || ""}
                    onChange={(e) => setDebitValues(prev => ({ ...prev, [user.email]: e.target.value }))}
                  />
                  <button
                    onClick={() => handleTransaction(user.email, debitValues[user.email], "debit")}
                    disabled={isUpdating[user.email]}
                    className="px-4 bg-red-500 text-white rounded-xl text-sm font-bold hover:bg-red-600 transition-all shadow-sm flex items-center justify-center min-w-[80px]"
                  >
                    {isUpdating[user.email] ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Debit'}
                  </button>
                </div>

                <div className="flex gap-2 flex-grow">
                  <input
                    type="number"
                    placeholder="Credit"
                    className="w-full px-3 py-2.5 bg-green-50/30 border border-green-100 rounded-xl focus:ring-2 focus:ring-green-500/20 outline-none text-sm font-bold text-green-700 placeholder-green-300 shadow-sm"
                    value={creditValues[user.email] || ""}
                    onChange={(e) => setCreditValues(prev => ({ ...prev, [user.email]: e.target.value }))}
                  />
                  <button
                    onClick={() => handleTransaction(user.email, creditValues[user.email], "credit")}
                    disabled={isUpdating[user.email]}
                    className="px-4 bg-green-500 text-white rounded-xl text-sm font-bold hover:bg-green-600 transition-all shadow-sm flex items-center justify-center min-w-[80px]"
                  >
                    {isUpdating[user.email] ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Credit'}
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-gray-50 rounded-2xl border border-dashed border-gray-200 py-20 flex flex-col items-center justify-center">
            <p className="text-gray-400 font-medium">No wallet records found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && walletData.length > 0 && (
        <div className="flex flex-col md:flex-row justify-between items-center mt-10 gap-6">
          <div className="text-xs font-bold text-gray-400 uppercase tracking-tight order-2 md:order-1">
            Page {pagination.page} / {pagination.totalPages}
          </div>
          <div className="flex space-x-2 order-1 md:order-2">
            <button
              className={`p-2 rounded-xl border transition-all ${pagination.page === 1 ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : 'hover:bg-white text-[#523680] border-[#523680]/20 shadow-sm'}`}
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            {/* Page numbers */}
            <div className="flex space-x-1.5 overflow-x-auto no-scrollbar max-w-[150px] sm:max-w-none px-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNumber;
                if (pagination.totalPages <= 5) pageNumber = i + 1;
                else if (pagination.page <= 3) pageNumber = i + 1;
                else if (pagination.page >= pagination.totalPages - 2) pageNumber = pagination.totalPages - 4 + i;
                else pageNumber = pagination.page - 2 + i;

                return (
                  <button
                    key={i}
                    className={`h-9 w-9 md:h-10 md:w-10 flex items-center justify-center rounded-xl font-bold text-xs md:text-sm transition-all shrink-0 ${pagination.page === pageNumber
                      ? 'bg-[#523680] text-white shadow-lg'
                      : 'border border-gray-100 hover:border-[#523680]/20 text-[#523680] bg-white'
                      }`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              className={`p-2 rounded-xl border transition-all ${pagination.page === pagination.totalPages ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : 'hover:bg-white text-[#523680] border-[#523680]/20 shadow-sm'}`}
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Wallet History</h2>
                <p className="text-sm text-gray-500">{selectedUserEmail}</p>
              </div>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-600 transition-all"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto p-6">
              {isHistoryLoading && historyData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-10 h-10 border-4 border-[#523680] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : historyData.length > 0 ? (
                <div className="space-y-4">
                  <div className="hidden md:block overflow-x-auto rounded-2xl border border-gray-100">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Amount</th>
                          <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Previous</th>
                          <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Updated</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {historyData.map((item) => (
                          <tr key={item._id} className="hover:bg-gray-50/50 transition-all">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
                              {new Date(item.createdAt).toLocaleString("en-GB", { dateStyle: 'medium', timeStyle: 'short' })}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${item.updateType === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {item.updateType}
                              </span>
                            </td>
                            <td className={`px-6 py-4 text-sm font-bold text-right ${item.updateType === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                              {item.updateType === 'credit' ? '+' : '-'}${parseFloat(item.updateAmount).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-400 text-right font-medium">
                              ${parseFloat(item.previousAmount).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 font-bold text-right">
                              ${parseFloat(item.updatedAmount).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="md:hidden space-y-3">
                    {historyData.map((item) => (
                      <div key={item._id} className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                        <div className="flex justify-between items-start mb-3">
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                            {new Date(item.createdAt).toLocaleString("en-GB", { dateStyle: 'medium', timeStyle: 'short' })}
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-tighter ${item.updateType === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {item.updateType}
                          </span>
                        </div>
                        <div className="flex justify-between items-end">
                          <div className="space-y-1">
                            <p className="text-[10px] text-gray-400 font-medium italic">Prev: ${parseFloat(item.previousAmount).toFixed(2)}</p>
                            <p className="text-xs text-gray-600 font-bold">New: ${parseFloat(item.updatedAmount).toFixed(2)}</p>
                          </div>
                          <div className={`text-lg font-black ${item.updateType === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                            {item.updateType === 'credit' ? '+' : '-'}${parseFloat(item.updateAmount).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between items-center bg-gray-50 p-4 rounded-2xl border border-gray-100 mt-4">
                    <span className="text-sm font-medium text-gray-500">
                      Page <span className="text-gray-900 font-bold">{historyPagination.page}</span> of {historyPagination.totalPages}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleHistoryPageChange(historyPagination.page - 1)}
                        disabled={historyPagination.page === 1}
                        className="p-2 border border-gray-200 rounded-lg bg-white disabled:opacity-40"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleHistoryPageChange(historyPagination.page + 1)}
                        disabled={historyPagination.page === historyPagination.totalPages}
                        className="p-2 border border-gray-200 rounded-lg bg-white disabled:opacity-40"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-20 flex flex-col items-center justify-center">
                  <History className="h-12 w-12 text-gray-200 mb-4" />
                  <p className="text-gray-400 font-medium">No records found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletUpdate;
